import { useMemo } from 'react';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { useTranslation } from 'next-i18next';

import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import { type CategoryTree } from '@/shared/category/type';
import { useCategoryTreeConversion } from '@/views/build/features/category-tree';
import { type PublisherInfo } from '@/views/build/type';

import { type CategoryInfoInputType } from '../../category-info/types';
import { type CategoryLabels } from '../types';
import { generateCategoryLabels } from '../utils';

/**
 * 카테고리 정보 label 생성 hook (Read-only)
 *
 * Form values를 받아서 각 카테고리의 display label을 생성합니다.
 * 상태를 관리하지 않고 순수하게 label만 계산합니다.
 *
 * @example
 * const labels = useCategoryLabels({
 *   formValues: { schoolTypeId: '101', gradeId: '1', ... },
 *   schoolInfoTree,
 *   subjectTree,
 *   publishers,
 *   userCountryCode: 'KR'
 * });
 *
 */
export const useCategoryLabels = ({
  categoryValues,
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
}: {
  /** Form values (카테고리 정보) */
  categoryValues?: CategoryInfoInputType;
  /** 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** 출판사 목록 (국가별) */
  publishers?: PublisherInfo[];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;
}): CategoryLabels => {
  const { t } = useTranslation(['common']);
  const { categoryToMenuItem } = useCategoryTreeConversion();

  /**
   * subjectTree를 CategoryNode로 변환하여 캐싱
   */
  const subjectNodes = useMemo(() => {
    return subjectTree.map(node => categoryToMenuItem(node));
  }, [subjectTree, categoryToMenuItem]);

  return useMemo(() => {
    if (!categoryValues) {
      return {
        schoolTypeLabel: undefined,
        gradeLabel: undefined,
        semesterLabel: undefined,
        subjectLabel: undefined,
        publisherLabel: undefined,
      };
    }

    return generateCategoryLabels({
      categoryValues,
      schoolInfoTree,
      subjectNodes,
      publishers,
      userCountryCode,
      t,
    });
  }, [
    categoryValues,
    schoolInfoTree,
    subjectNodes,
    publishers,
    userCountryCode,
    t,
  ]);
};
